import React from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import type { OrderDetailResponse, OrderStatus } from '../../types/api';
import { formatSide, formatStatus, formatOrderType, formatTimeInForce } from '../../utils/orderFormat';

interface OrderDetailModalProps {
  order: OrderDetailResponse;
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  onAmend: () => void;
}

/**
 * Statuses that can be canceled/amended
 */
const CANCELLABLE_STATUSES: OrderStatus[] = ['pending_new', 'accepted', 'new', 'partially_filled'];

/**
 * Check if an order can be canceled or amended
 */
function isCancellable(status: OrderStatus): boolean {
  return CANCELLABLE_STATUSES.includes(status);
}

/**
 * Formats a currency value for display
 */
function formatCurrency(value: string | undefined): string {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Formats a date for display
 */
function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

/**
 * Status badge color configuration
 */
const STATUS_STYLES: Record<OrderStatus, { bg: string; text: string }> = {
  pending_new: { bg: 'bg-gray-100', text: 'text-gray-800' },
  accepted: { bg: 'bg-blue-100', text: 'text-blue-800' },
  new: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  partially_filled: { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  filled: { bg: 'bg-green-100', text: 'text-green-800' },
  canceled: { bg: 'bg-gray-100', text: 'text-gray-500' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800' },
  pending_cancel: { bg: 'bg-orange-100', text: 'text-orange-800' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500' },
  suspended: { bg: 'bg-gray-100', text: 'text-gray-500' },
  pending_replace: { bg: 'bg-purple-100', text: 'text-purple-800' },
  replaced: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

/**
 * Timeline event type
 */
interface TimelineEvent {
  label: string;
  timestamp: string | undefined;
  description?: string;
  isComplete: boolean;
  isCurrent: boolean;
}

/**
 * Build timeline events from order data
 */
function buildTimeline(order: OrderDetailResponse): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Created
  events.push({
    label: 'Created',
    timestamp: order.created_at,
    isComplete: true,
    isCurrent: false,
  });

  // Submitted
  if (order.submitted_at) {
    events.push({
      label: 'Submitted',
      timestamp: order.submitted_at,
      isComplete: true,
      isCurrent: false,
    });
  }

  // Status-specific events
  const filledQty = parseFloat(order.filled_quantity);
  const totalQty = parseFloat(order.quantity);

  if (filledQty > 0 && filledQty < totalQty) {
    events.push({
      label: 'Partially Filled',
      timestamp: order.updated_at,
      description: `${order.filled_quantity} shares filled`,
      isComplete: true,
      isCurrent: order.status === 'partially_filled',
    });
  }

  if (order.filled_at) {
    events.push({
      label: 'Filled',
      timestamp: order.filled_at,
      description: `${order.filled_quantity} shares`,
      isComplete: true,
      isCurrent: order.status === 'filled',
    });
  }

  if (order.canceled_at) {
    events.push({
      label: 'Canceled',
      timestamp: order.canceled_at,
      isComplete: true,
      isCurrent: order.status === 'canceled',
    });
  }

  if (order.expired_at) {
    events.push({
      label: 'Expired',
      timestamp: order.expired_at,
      isComplete: true,
      isCurrent: order.status === 'expired',
    });
  }

  if (order.status === 'rejected') {
    events.push({
      label: 'Rejected',
      timestamp: order.updated_at,
      description: order.rejection_reason,
      isComplete: true,
      isCurrent: true,
    });
  }

  if (order.status === 'replaced') {
    events.push({
      label: 'Replaced',
      timestamp: order.updated_at,
      isComplete: true,
      isCurrent: true,
    });
  }

  // Awaiting fill (for active orders)
  if (isCancellable(order.status) && filledQty < totalQty) {
    events.push({
      label: 'Awaiting Fill',
      timestamp: undefined,
      description: `${order.remaining_quantity} shares remaining`,
      isComplete: false,
      isCurrent: true,
    });
  }

  return events;
}

/**
 * Order Detail Modal component
 * Shows full order details with timeline
 */
export function OrderDetailModal({
  order,
  isOpen,
  onClose,
  onCancel,
  onAmend,
}: OrderDetailModalProps) {
  const statusStyle = STATUS_STYLES[order.status] || STATUS_STYLES.pending_new;
  const timeline = buildTimeline(order);
  const canCancel = isCancellable(order.status);
  const canAmend = canCancel && order.order_type === 'limit';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Order Details">
      <div className="space-y-6">
        {/* Order IDs */}
        <div className="space-y-1">
          <p className="text-sm text-gray-500">
            Client Order ID:{' '}
            <span className="font-mono text-gray-900">{order.client_order_id}</span>
          </p>
          <p className="text-sm text-gray-500">
            Order ID: <span className="font-mono text-gray-900">{order.id}</span>
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-lg font-semibold text-gray-900">
            {formatSide(order.side)} {order.quantity} {order.symbol}{' '}
            {order.order_type === 'limit' && order.limit_price && (
              <>@ Limit ${order.limit_price}</>
            )}
            {order.order_type === 'market' && '@ Market'}
          </div>
          <div className="mt-2 flex items-center space-x-3">
            <span
              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
            >
              {formatStatus(order.status)}
            </span>
            <span className="text-sm text-gray-600">
              Filled: {order.filled_quantity}/{order.quantity}
            </span>
          </div>
        </div>

        {/* Order Details */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
            Order Details
          </h4>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-gray-500">Symbol</dt>
              <dd className="font-medium text-gray-900">{order.symbol}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Side</dt>
              <dd className="font-medium text-gray-900">{formatSide(order.side)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Order Type</dt>
              <dd className="font-medium text-gray-900">{formatOrderType(order.order_type)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Time in Force</dt>
              <dd className="font-medium text-gray-900">{formatTimeInForce(order.time_in_force)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Quantity</dt>
              <dd className="font-medium text-gray-900">{order.quantity}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Filled Quantity</dt>
              <dd className="font-medium text-gray-900">{order.filled_quantity}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Remaining</dt>
              <dd className="font-medium text-gray-900">{order.remaining_quantity}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Limit Price</dt>
              <dd className="font-medium text-gray-900">
                {order.limit_price ? formatCurrency(order.limit_price) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Extended Hours</dt>
              <dd className="font-medium text-gray-900">
                {order.extended_hours ? 'Yes' : 'No'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Buying Power Held</dt>
              <dd className="font-medium text-gray-900">
                {formatCurrency(order.buying_power_withheld)}
              </dd>
            </div>
          </dl>
        </div>

        {/* Timeline */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
            Timeline
          </h4>
          <div className="relative">
            {timeline.map((event, index) => (
              <div key={index} className="flex items-start mb-4 last:mb-0">
                {/* Connector line */}
                {index < timeline.length - 1 && (
                  <div
                    className={`absolute left-[7px] top-4 w-0.5 h-[calc(100%-1rem)] ${
                      event.isComplete ? 'bg-gray-300' : 'bg-gray-200'
                    }`}
                    style={{ top: `${index * 44 + 16}px`, height: '28px' }}
                  />
                )}
                {/* Icon */}
                <div
                  className={`flex-shrink-0 h-4 w-4 rounded-full border-2 mt-0.5 ${
                    event.isComplete
                      ? event.isCurrent
                        ? 'bg-blue-500 border-blue-500'
                        : 'bg-green-500 border-green-500'
                      : 'bg-white border-gray-300'
                  }`}
                />
                {/* Content */}
                <div className="ml-3 min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-medium ${
                        event.isCurrent ? 'text-blue-600' : 'text-gray-900'
                      }`}
                    >
                      {event.label}
                    </span>
                    {event.timestamp && (
                      <span className="text-xs text-gray-500">
                        {formatDateTime(event.timestamp)}
                      </span>
                    )}
                  </div>
                  {event.description && (
                    <p className="mt-0.5 text-xs text-gray-500">{event.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rejection Reason */}
        {order.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              <span className="font-medium">Rejection Reason:</span>{' '}
              {order.rejection_reason}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          {canCancel && (
            <Button variant="danger" onClick={onCancel}>
              Cancel Order
            </Button>
          )}
          {canAmend && (
            <Button variant="secondary" onClick={onAmend}>
              Amend Order
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
